<template>
  <div class="task-canvas-page">
         <h1>Task Canvas</h1>
     <p>Design and manage your task workflows with interactive canvases</p>
     
     <!-- Debug Info -->
     <div class="debug-info">
       <p><strong>Current Tab:</strong> {{ activeTab }}</p>
       <p><strong>Input Items:</strong> {{ inputItems.length }} | <strong>Workflow Items:</strong> {{ workflowItems.length }} | <strong>Output Items:</strong> {{ outputItems.length }}</p>
       <p><strong>Current Canvas Data:</strong> {{ currentCanvasData.length }} items</p>
       <p><strong>Canvas Data Preview:</strong> {{ currentCanvasData.map(item => item.title).join(', ') || 'None' }}</p>
     </div>
    
    <!-- Simple Header -->
    <div class="header">
      <h2>Canvas Test</h2>
    </div>
    
    <!-- Simple Tabs -->
         <div class="tabs">
       <button @click="switchTab('input')" :class="{active: activeTab === 'input'}">
         Input
         <span v-if="inputItems.length > 0" class="tab-badge">{{ inputItems.length }}</span>
       </button>
       <button @click="switchTab('workflow')" :class="{active: activeTab === 'workflow'}">
         Workflow
         <span v-if="workflowItems.length > 0" class="tab-badge">{{ workflowItems.length }}</span>
       </button>
       <button @click="switchTab('output')" :class="{active: activeTab === 'output'}">
         Output
         <span v-if="outputItems.length > 0" class="tab-badge">{{ outputItems.length }}</span>
       </button>
     </div>
    
         <!-- Tab Content -->
     <div class="tab-content" :key="activeTab">
       <div v-if="activeTab === 'input'">
         <div class="canvas-container">
           <BasicCanvas
             key="input-canvas"
             :inputData="inputItems"
             @items-updated="handleInputUpdated"
           />
         </div>
         <div class="canvas-actions">
           <el-button size="small" @click="addSampleInput" type="primary">
             <i class="el-icon-plus"></i>
             Add Sample Input
           </el-button>
         </div>
      </div>
      <div v-if="activeTab === 'workflow'">
         <div class="canvas-container">
           <BasicWorkflowCanvas
             key="workflow-canvas"
             :inputData="workflowItems"
             @items-updated="handleWorkflowUpdated"
           />
         </div>
         <div class="canvas-actions">
           <el-button size="small" @click="addWorkflowStep" type="primary">
             <i class="el-icon-plus"></i>
             Add Workflow Step
           </el-button>
         </div>
       </div>
       <div v-if="activeTab === 'output'">
         <div class="canvas-container">
           <BasicOutputCanvas
             key="output-canvas"
             :inputData="outputItems"
             @items-updated="handleOutputUpdated"
           />
         </div>
         <div class="canvas-actions">
           <el-button size="small" @click="addSampleOutput" type="primary">
             <i class="el-icon-plus"></i>
             Add Sample Output
           </el-button>
         </div>
       </div>
    </div>
  </div>
</template>

<script>
import BasicCanvas from '@/components/BasicCanvas.vue';
import BasicWorkflowCanvas from '@/components/BasicWorkflowCanvas.vue';
import BasicOutputCanvas from '@/components/BasicOutputCanvas.vue';

export default {
  name: 'TaskCanvas',
  components: {
    BasicCanvas,
    BasicWorkflowCanvas,
    BasicOutputCanvas
  },
      data() {
      return {
        activeTab: 'input',
        inputItems: [],
        workflowItems: [],
        outputItems: []
      };
    },
  mounted() {
    this.loadSampleData();
  },
  computed: {
    currentCanvasData() {
      switch (this.activeTab) {
        case 'input':
          return this.inputItems;
        case 'workflow':
          return this.workflowItems;
        case 'output':
          return this.outputItems;
        default:
          return [];
      }
    }
  },
  methods: {
    switchTab(tabName) {
      console.log('Switching to tab:', tabName);
      this.activeTab = tabName;
      console.log('Active tab is now:', this.activeTab);
      
      // Force a re-render by updating the data
      this.$nextTick(() => {
        console.log('Tab switched, current activeTab:', this.activeTab);
        console.log('Input items:', this.inputItems);
        console.log('Workflow items:', this.workflowItems);
        console.log('Output items:', this.outputItems);
        
        // Force refresh the current canvas data
        this.$forceUpdate();
      });
    },

    loadSampleData() {
      this.inputItems = [
        {
          id: 'sample_1',
          title: 'Sample Input Card',
          content: 'This is a test input card',
          content_type: 'text',
          created_at: Date.now(),
          labels: ['test', 'input']
        }
      ];

      this.workflowItems = [
        {
          id: 'workflow_1',
          title: 'Data Transform Step',
          content: 'Transform input data for processing',
          content_type: 'workflow',
          created_at: Date.now(),
          labels: ['transform', 'workflow']
        }
      ];

      this.outputItems = [
        {
          id: 'output_1',
          title: 'Generated Result',
          content: 'This is a sample output result',
          content_type: 'output',
          created_at: Date.now(),
          labels: ['result', 'output']
        }
      ];
    },
    
    handleInputUpdated(items) {
      this.inputItems = items;
      console.log('Input updated:', items);
    },

    handleWorkflowUpdated(items) {
      this.workflowItems = items;
      console.log('Workflow updated:', items);
    },

    handleOutputUpdated(items) {
      this.outputItems = items;
      console.log('Output updated:', items);
    },

    addSampleInput() {
      const newInput = {
        id: `input_${Date.now()}`,
        title: `Sample Input ${this.inputItems.length + 1}`,
        content: 'This is a sample input item',
        content_type: 'text',
        created_at: Date.now(),
        labels: ['sample', 'input']
      };
      this.inputItems.push(newInput);
      this.$message.success('Sample input added!');
    },

    addWorkflowStep() {
      const newStep = {
        id: `workflow_${Date.now()}`,
        title: `Workflow Step ${this.workflowItems.length + 1}`,
        content: 'This is a workflow step',
        content_type: 'workflow',
        created_at: Date.now(),
        labels: ['workflow', 'step']
      };
      this.workflowItems.push(newStep);
      this.$message.success('Workflow step added!');
    },

    addSampleOutput() {
      const newOutput = {
        id: `output_${Date.now()}`,
        title: `Sample Output ${this.outputItems.length + 1}`,
        content: 'This is a sample output item',
        content_type: 'output',
        created_at: Date.now(),
        labels: ['sample', 'output']
      };
      this.outputItems.push(newOutput);
      this.$message.success('Sample output added!');
    }
  }
};
</script>

<style scoped>
.task-canvas-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
  padding: 20px;
}

 .header {
   background: white;
   padding: 20px;
   border-radius: 8px;
   margin-bottom: 20px;
   box-shadow: 0 2px 4px rgba(0,0,0,0.1);
 }

 .debug-info {
   background: #f0f9ff;
   border: 1px solid #0ea5e9;
   border-radius: 6px;
   padding: 12px;
   margin-bottom: 16px;
   font-size: 14px;
   color: #0369a1;
 }

 .debug-info p {
   margin: 4px 0;
 }

.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tabs button {
  padding: 10px 20px;
  border: none;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.3s;
}

.tabs button.active {
  background: #409eff;
  color: white;
}

 .tabs button:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 8px rgba(0,0,0,0.15);
 }

 .tabs button {
   position: relative;
 }

 .tab-badge {
   position: absolute;
   top: -8px;
   right: -8px;
   background: #f56c6c;
   color: white;
   border-radius: 50%;
   width: 20px;
   height: 20px;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 12px;
   font-weight: 600;
 }

.tab-content {
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

 .canvas-container {
   height: 600px;
   width: 100%;
   background: #f8f9fa;
   border-radius: 6px;
   border: 1px solid #ebeef5;
 }

 .canvas-actions {
   margin-top: 16px;
   padding: 16px;
   background: #f8f9fa;
   border-radius: 6px;
   border: 1px solid #ebeef5;
   text-align: center;
 }

.task-canvas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #ebeef5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.task-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.task-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.task-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.task-id {
  font-size: 12px;
  color: #909399;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-container {
  background: white;
  border-bottom: 1px solid #ebeef5;
}

.canvas-tabs {
  padding: 0 24px;
}

.canvas-tabs >>> .el-tabs__header {
  margin: 0;
}

.tab-badge {
  margin-left: 8px;
}

.canvas-content {
  flex: 1;
  overflow: hidden;
}

.canvas-view {
  height: 100%;
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  text-align: center;
}

.placeholder-content h3 {
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 500;
}

.placeholder-content p {
  margin: 0;
  font-size: 16px;
}

@media (max-width: 768px) {
  .task-canvas-header {
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
  }

  .header-left {
    width: 100%;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>